/**
 * The chat icon beside the bell in every page header (decided 12 Oct 2026:
 * its own icon and count, not merged with the bell). On a desktop it opens
 * the quick peek; on a phone it goes to the Messages page.
 * Rendered by NotificationBell, so it shows wherever the bell does.
 */
import React, { Suspense, lazy, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useMessagesUnread } from '../nav/MessagesNavCount';
import { hapticTick } from '../../../_utils/haptics';
import { rememberPage } from '../support/supportContext';
import { actions, getState } from '../data/store';
import { canSeeSpace } from '../data/spaces';
import './header-button.css';

// The peek carries the whole chat; load it the first time it is opened.
const MessagesPeek = lazy(() => import('./MessagesPeek'));

const handlers = [];
window.addEventListener('bm:peek', (e) => {
    const handler = handlers[handlers.length - 1];
    if (handler) handler((e && e.detail) || {});
});

/**
 * The fallback, for pages whose header has no Messages button (the diary):
 * MessagesGlobal answers there. Any header button added later wins.
 */
export function addFallbackPeekHandler(handler) {
    handlers.unshift(handler);
    return () => { const i = handlers.indexOf(handler); if (i !== -1) handlers.splice(i, 1); };
}

const ChatIcon = () => (
    <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
    </svg>
);

export default function MessagesHeaderButton({ className = '' }) {
    const unread = useMessagesUnread();
    const history = useHistory();
    const [peek, setPeek] = useState(null); // { chatId, card }

    // Support messages say which page the salon was on (see supportContext).
    useEffect(() => { rememberPage(history.location.pathname); return history.listen((loc) => rememberPage(loc.pathname)); }, [history]);

    // "Ask the team" and other screens open the peek through an event. Some
    // headers render the bell twice (one per breakpoint), so only the newest
    // button answers, and only one peek ever opens.
    useEffect(() => {
        const handler = (d) => {
            // "Message client": find (or start) their chat, then open it
            if (d.client) {
                if (!canSeeSpace('clients')) return;
                actions.openClientChat(d.client).then((c) => {
                    if (window.innerWidth < 768) history.push(`/messages/clients/${c.id}`);
                    else setPeek({ chatId: c.id, card: null });
                });
                return;
            }
            const c = d.conversationId && getState().conversations[d.conversationId];
            if (c && !canSeeSpace(c.space)) return;
            if (window.innerWidth < 768) { history.push(d.conversationId ? `/messages/team/${d.conversationId}` : '/messages'); return; }
            setPeek({ chatId: d.conversationId || null, card: d.card || null });
        };
        handlers.push(handler);
        return () => { const i = handlers.indexOf(handler); if (i !== -1) handlers.splice(i, 1); };
    }, [history]);

    const open = () => {
        hapticTick();
        if (window.innerWidth < 768) { history.push('/messages'); return; }
        setPeek(peek ? null : { chatId: null, card: null });
    };

    return (
        <>
            <button type="button" className={`bm-head-btn ${className}${peek ? ' is-on' : ''}`} onClick={open}
                aria-label={unread ? `Messages, ${unread} unread` : 'Messages'}>
                <ChatIcon />
                {unread > 0 && <span className="bm-head-count">{unread > 9 ? '9+' : unread}</span>}
            </button>
            {peek && (
                <Suspense fallback={null}>
                    <MessagesPeek initialChatId={peek.chatId} initialCard={peek.card} onClose={() => setPeek(null)} />
                </Suspense>
            )}
        </>
    );
}
